import styled from '@emotion/styled';
import EmailAddressMultiselect from 'components/ui/EmailAddressMultiselect';
import createDOMpurify from 'dompurify';
import React from 'react';
import { Checkbox, Input } from 'semantic-ui-react';
import { ContactOption, FileUpload } from './types';
import VendorEmailAttachments from './VendorEmailAttachments';

interface PropTypes {
  disabled: boolean;
  contactsList: ContactOption[];
  selectedContacts: ContactOption[];
  selectedCcEmails: ContactOption[];
  selectedBccEmails: ContactOption[];
  subject: string;
  emailBodyPreview: string;
  scheduleSend: boolean;
  scheduleSendDateTime: string;
  scheduleSendMin: string;
  setSelectedContacts: (selections: ContactOption[]) => void;
  setSelectedCcEmails: (selections: ContactOption[]) => void;
  setSelectedBccEmails: (selections: ContactOption[]) => void;
  setSubject: (subject: string) => void;
  handleScheduleSendCheck: (checked: boolean) => void;
  setScheduleSendDateTime: (dateTime: string) => void;
  sendEmailChecked: boolean;
  handleSendEmailCheck: (checked: boolean) => void;
  requestNotes: React.ReactNode;
  attachments: FileUpload[];
  setAttachments: (attachments: FileUpload[]) => void;
}

const InputLabel = styled.span<{ disabled: boolean }>`
  font-size: 14px;
  color: ${props => (props.disabled ? 'grey' : '#444444')};
`;

const EmailFieldsContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(360px, 520px);
  column-gap: 40px;
  row-gap: 16px;
  align-items: stretch;
  max-width: 940px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(280px, 1fr);
    max-width: 100%;
  }
`;

const EmailColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const SubjectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 38px;
`;

const BottomInlineRow = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(360px, 520px);
  align-items: flex-start;
  column-gap: 40px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 16px;
    column-gap: 0;
  }
`;

const ScheduleControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  min-height: 38px;
`;

const ScheduleDateTimeSlot = styled.div<{ visible: boolean }>`
  flex: 0 0 220px;
  width: 220px;
  visibility: ${props => (props.visible ? 'visible' : 'hidden')};
`;

const InlineScheduleRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-top: 8px;
  justify-self: start;

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const EmailPreviewSection = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-top: 16px;
  min-height: 0;
`;

const EmailPreview = styled.div<{ disabled: boolean }>`
  flex: 1;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  background: #f8f8f8;
  min-height: 120px;
  overflow-y: auto;
  padding: 10px 12px;
  color: #333333;
  opacity: ${props => (props.disabled ? '0.55' : '1')};
  line-height: 1.35;
`;

const EmailFields = (props: PropTypes) => {
  return (
    <EmailFieldsContainer>
      <EmailColumn>
        <EmailAddressMultiselect
          title={'Send to:'}
          width={'84%'}
          contactsList={props.contactsList}
          onChangeSelections={(selections: any) => props.setSelectedContacts(selections ?? [])}
          selectedContactsList={props.selectedContacts}
          placeholder={'Select Contacts'}
          disabled={props.disabled}
        />
        <EmailAddressMultiselect
          title={'CC:'}
          width={'84%'}
          contactsList={[]}
          onChangeSelections={selections => props.setSelectedCcEmails(selections ?? [])}
          selectedContactsList={props.selectedCcEmails}
          placeholder={'Select CC Emails'}
          disabled={props.disabled}
        />
        <EmailAddressMultiselect
          title={'BCC:'}
          width={'84%'}
          contactsList={[]}
          onChangeSelections={selections => props.setSelectedBccEmails(selections ?? [])}
          selectedContactsList={props.selectedBccEmails}
          placeholder={'Select BCC Emails'}
          disabled={props.disabled}
        />
        <EmailPreviewSection>
          <InputLabel disabled={props.disabled}>Email Preview:</InputLabel>
          <EmailPreview
            disabled={props.disabled}
            dangerouslySetInnerHTML={{ __html: createDOMpurify.sanitize(props.emailBodyPreview || '') }}
          />
        </EmailPreviewSection>
      </EmailColumn>
      <EmailColumn style={{ zIndex: 100 }}>
        <SubjectRow>
          <InputLabel disabled={props.disabled} css={{ minWidth: 58 }}>
            Subject:
          </InputLabel>
          <Input
            basic
            value={props.subject}
            onChange={e => props.setSubject(e.target.value)}
            style={{ width: '100%' }}
            disabled={props.disabled}
          />
        </SubjectRow>
        {props.requestNotes}
      </EmailColumn>
      <BottomInlineRow>
        <InlineScheduleRow>
          <Checkbox
            checked={props.sendEmailChecked}
            onChange={(_, { checked }) => props.handleSendEmailCheck(!!checked)}
            label="Send Email to Vendor on Submit"
          />
          <ScheduleControls>
            <Checkbox
              checked={props.scheduleSend}
              onChange={(e, { checked }) => props.handleScheduleSendCheck(checked)}
              label="Schedule Send"
              disabled={props.disabled}
            />
            <ScheduleDateTimeSlot visible={props.scheduleSend}>
              <Input
                type="datetime-local"
                value={props.scheduleSendDateTime}
                onChange={e => props.setScheduleSendDateTime(e.target.value)}
                disabled={props.disabled || !props.scheduleSend}
                min={props.scheduleSendMin}
                style={{ width: '100%' }}
              />
            </ScheduleDateTimeSlot>
          </ScheduleControls>
        </InlineScheduleRow>
        <VendorEmailAttachments
          attachments={props.attachments}
          setAttachments={props.setAttachments}
          disabled={!props.sendEmailChecked}
          variant="basic"
        />
      </BottomInlineRow>
    </EmailFieldsContainer>
  );
};

export default EmailFields;
